import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause, faPlay, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import {
  checkBGValue,
  dedupeMeasurementsByDateTimeKeepLatest,
  extractWallClockDateYmd,
  formatMeasurementTableDate,
  formatMeasurementTableTime,
  getMeasurementValueClass,
  getMeasurementWallClockDateYmd,
  getMeasurementWallClockTimeHms,
  parseWallClockDateLocal,
  checkWeightValue
} from '../../../utils/measurementUtils';
import measurementService from '../../../services/measurementService';
import patientService from '../../../services/patientService';
import { toast } from 'react-toastify';
import AddBGForm from './AddBGForm';
import AddFollowUpModal from '../../../components/AddFollowUpModal/AddFollowUpModal';
import VitalsTabs from '../../../components/MeasurementData/VitalsTabs/VitalsTabs';
import EmailModal from '../../../components/EmailModal/EmailModal';
import { sendReportEmail } from '../../../utils/reportEmailSend';
import { downloadBlob, printElement, toCsv } from '../../../utils/reportExportUtils';
import { exportBloodGlucoseTablesPdf } from '../../../utils/measurementTablePdfExport';
import {
  findPeriodByName,
  hasAnyScheduleTargets,
  normalizePeriodName,
  resolvePeriodNameByIdOrLabel,
  resolveDisplayScheduleTargets,
  getMeasurementEpochMs
} from '../../../utils/scheduleTargetUtils';
import {
  PATIENT_SCHEDULE_TARGET_PREF_EVENT,
  getUsePatientScheduleTargetsAtMeasurementMs,
  getUsePatientScheduleTargetsPreference
} from '../../../utils/patientScheduleTargetPreference.js';
import { formatPhoneForDisplay } from '../../../utils/phoneUtils';
import {
  canAddPracticeVital,
  MEASURED_TYPE_IDS,
  normalizePracticeMonitoringTypes,
  PRACTICE_VITAL_ADD_DISABLED_TITLE
} from '../../../utils/practiceMonitoringUtils';
import {
  DEFAULT_MEASUREMENT_QUERY,
  applyMeasurementFromDateChange,
  getMeasurementQueryMaxDate,
  isFromToIncomplete,
  isMeasurementQueryDirty,
  patchOnQueryModeChange,
  sanitizeMeasurementToDate,
} from '../../../utils/queryPeriodFilters';
import AppDateInput from '../../../components/Common/AppDateInput/AppDateInput';
import { useMeasurementSessionOptional } from '../MeasurementControlsBar';
import {
  PERMANENT_END_TIMES,
  PERMANENT_START_TIMES,
  determinePeriodFromTime,
  getPermanentPeriodDisplay
} from '../../../utils/measurementPeriodUtils';
import './BloodGlucose.css';
import '../MeasurementShell.css';

const BloodGlucose = ({ practiceId, patientId, practiceIsLocked = false, practiceMonitoringTypes = null }) => {
  const { updateBreadcrumbs } = useBreadcrumb();
  const location = useLocation();
  const session = useMeasurementSessionOptional();
  const useSharedControls = Boolean(session);

  const [localTimer, setLocalTimer] = useState(0);
  const [localIsRunning, setLocalIsRunning] = useState(false);
  const timer = useSharedControls ? session.timer : localTimer;
  const setTimer = useSharedControls ? session.setTimer : setLocalTimer;
  const isRunning = useSharedControls ? session.isRunning : localIsRunning;
  const setIsRunning = useSharedControls ? session.setIsRunning : setLocalIsRunning;
  const [queryType, setQueryType] = useState(DEFAULT_MEASUREMENT_QUERY.queryType);
  const [dateRange, setDateRange] = useState(DEFAULT_MEASUREMENT_QUERY.dateRange);
  const [fromDate, setFromDate] = useState(DEFAULT_MEASUREMENT_QUERY.fromDate);
  const [toDate, setToDate] = useState(DEFAULT_MEASUREMENT_QUERY.toDate);
  const [sortBy, setSortBy] = useState(DEFAULT_MEASUREMENT_QUERY.sortBy);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [scheduleTargetSources, setScheduleTargetSources] = useState(null);
  const [targetTimelineRev, setTargetTimelineRev] = useState(0);
  const [loading, setLoading] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [hasFetchedMeasurements, setHasFetchedMeasurements] = useState(false);
  const [hasFetchedPatientData, setHasFetchedPatientData] = useState(false);
  const [hasFetchedScheduleTargets, setHasFetchedScheduleTargets] = useState(false);
  const patientVitals = patientData?.patient?.vitals || '';
  const resolvedPracticeTypes = useMemo(() => {
    if (Array.isArray(practiceMonitoringTypes)) return practiceMonitoringTypes;
    return normalizePracticeMonitoringTypes(patientData?.practice_monitoring_types);
  }, [practiceMonitoringTypes, patientData?.practice_monitoring_types]);
  const canAddThisVital = canAddPracticeVital({
    practiceIsLocked,
    practiceMonitoringTypes: resolvedPracticeTypes,
    typeId: MEASURED_TYPE_IDS.BG
  });
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hoverPreview, setHoverPreview] = useState(null);
  const initialized = useRef(false);
  const hoverHideTimerRef = useRef(null);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    if (practiceIsLocked) {
      setShowAddModal(false);
      setShowFollowUpModal(false);
      setIsRunning(false);
      setTimer(0);
    }
  }, [practiceIsLocked]);

  useEffect(() => {
    if (!canAddThisVital) setShowAddModal(false);
  }, [canAddThisVital]);

  // Fetch patient data and schedule targets on mount
  useEffect(() => {
    if (practiceId && patientId) {
      setHasFetchedPatientData(false);
      setHasFetchedScheduleTargets(false);
      setHasFetchedMeasurements(false);
      setScheduleTargetSources(null);
      if (!initialized.current) {
        if (!practiceIsLocked) {
          setIsRunning(true);
        }
        initialized.current = true;
      }
      fetchPatientData();
      fetchScheduleTargets();
    }
    return () => {
      initialized.current = false;
    };
  }, [practiceId, patientId]);

  // Fetch measurements on initial load / patient change only (filters apply via Query button).
  useEffect(() => {
    if (practiceId && patientId) {
      fetchMeasurements({ silent: true });
    }
  }, [practiceId, patientId]);

  // Timer effect (local only — shared timer lives in MeasurementControlsBar)
  useEffect(() => {
    if (useSharedControls) return undefined;
    let interval = null;
    if (isRunning) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, useSharedControls]);

  const formatTimer = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')} : ${secs.toString().padStart(2, '0')}`;
  };

  const fetchPatientData = async () => {
    const cacheKey = `measurement:patient:${practiceId}:${patientId}`;
    const cachedRaw = sessionStorage.getItem(cacheKey);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        if (cached && typeof cached === 'object') setPatientData(cached);
      } catch {
        // ignore
      }
    }
    try {
      const response = await patientService.getPatientDetailsFast(practiceId, patientId);
      if (response.data.success) {
        const next = response.data.data;
        setPatientData(next);
        sessionStorage.setItem(cacheKey, JSON.stringify(next));
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
    } finally {
      setHasFetchedPatientData(true);
    }
  };

  const fetchScheduleTargets = async () => {
    try {
      const sources = await patientService.getScheduleTargetSourcesForPatient(practiceId, patientId);
      setScheduleTargetSources(sources);
      const usePatientTargets = getUsePatientScheduleTargetsPreference(practiceId, patientId);
      const effective = resolveDisplayScheduleTargets({
        practiceData: sources.practiceData,
        patientData: sources.patientData,
        usePatientTargets
      });
      setScheduleTargets(hasAnyScheduleTargets(effective) ? effective : sources.practiceData);
    } catch (error) {
      console.error('Error fetching schedule targets:', error);
    } finally {
      setHasFetchedScheduleTargets(true);
    }
  };

  useEffect(() => {
    if (!practiceId || !patientId) return undefined;
    const onPref = (ev) => {
      const d = ev?.detail;
      if (String(d?.practiceId) !== String(practiceId) || String(d?.patientId) !== String(patientId)) return;
      setTargetTimelineRev((n) => n + 1);
      fetchScheduleTargets();
    };
    window.addEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
    return () => window.removeEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
  }, [practiceId, patientId]);

  const fetchMeasurements = async ({ silent = false } = {}) => {
    try {
      let fromDateParam, toDateParam;
      const isAllHistory = queryType === 'dateRange' && String(dateRange).toUpperCase() === 'ALL';

      if (queryType === 'dateRange') {
        if (!isAllHistory) {
          const days = parseInt(dateRange.replace(/\D/g, ''), 10) || 30;
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - days);
          fromDateParam = startDate.toISOString().split('T')[0];
          toDateParam = endDate.toISOString().split('T')[0];
        }
      } else {
        // Inputs are already YYYY-MM-DD from type="date"
        if (fromDate && toDate) {
          fromDateParam = fromDate;
          toDateParam = toDate;
        }
      }

      if (!isAllHistory && !(fromDateParam && toDateParam)) {
        return;
      }

      const queryCacheKey = `measurement:bg:${practiceId}:${patientId}:${queryType}:${dateRange}:${fromDateParam || ''}:${toDateParam || ''}:${sortBy}`;
      let hadCachedData = false;
      const cachedRaw = sessionStorage.getItem(queryCacheKey);
      if (cachedRaw) {
        try {
          const cached = JSON.parse(cachedRaw);
          if (Array.isArray(cached)) {
            setMeasurements(cached);
            setHasFetchedMeasurements(true);
            hadCachedData = true;
          }
        } catch {
          // ignore
        }
      }
      if (!silent || !hadCachedData) {
        setQueryLoading(true);
      }
      const sortKeyMap = {
        '1 Day Average': 'daily_average',
        'Weekly average': 'weekly_average',
        '14 Days Average': 'weekly_average',
        'Monthly average': 'monthly_average',
        '3 Months Average': 'monthly_average',
        'All': 'date',
        'Date': 'date',
        'Daily average': 'daily_average'
      };
      const response = await measurementService.getBloodGlucoseByDateRange(
        practiceId,
        patientId,
        isAllHistory ? undefined : fromDateParam,
        isAllHistory ? undefined : toDateParam,
        sortKeyMap[sortBy] || 'weekly_average'
      );

      if (response.data?.success) {
        const next = response.data.data || [];
        setMeasurements((prev) => (JSON.stringify(prev || []) === JSON.stringify(next) ? prev : next));
        sessionStorage.setItem(queryCacheKey, JSON.stringify(next));
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching measurements:', error);
      if (error.response?.status === 404) {
        console.log('Measurement API endpoint not yet available');
        setMeasurements([]);
      } else {
        toast.error('Failed to load measurements');
        setMeasurements([]);
      }
    } finally {
      setQueryLoading(false);
      setLoading(false);
      setHasFetchedMeasurements(true);
    }
  };

  const isQueryDisabled = queryLoading || isFromToIncomplete(queryType, fromDate, toDate);
  const isResetDisabled = queryLoading || !isMeasurementQueryDirty({
    queryType, dateRange, fromDate, toDate, sortBy,
  });

  const handleQueryTypeChange = (nextType) => {
    const patch = patchOnQueryModeChange(nextType);
    setQueryType(patch.queryType);
    if (Object.prototype.hasOwnProperty.call(patch, 'fromDate')) {
      setFromDate(patch.fromDate);
      setToDate(patch.toDate);
    }
  };

  const handleQuery = () => {
    if (isFromToIncomplete(queryType, fromDate, toDate)) {
      toast.warning('Please select both start and end dates');
      return;
    }
    fetchMeasurements({ silent: false });
  };

  const handleReset = () => {
    if (isResetDisabled) return;
    setQueryType(DEFAULT_MEASUREMENT_QUERY.queryType);
    setDateRange(DEFAULT_MEASUREMENT_QUERY.dateRange);
    setFromDate(DEFAULT_MEASUREMENT_QUERY.fromDate);
    setToDate(DEFAULT_MEASUREMENT_QUERY.toDate);
    setSortBy(DEFAULT_MEASUREMENT_QUERY.sortBy);
  };

  const handleAddDataSuccess = () => {
    fetchMeasurements({ silent: false });
  };

  const formatDate = (dateStr) => formatMeasurementTableDate(dateStr);

  const formatDateIso = (dateStr) => extractWallClockDateYmd(dateStr);

  const formatTime = (timeStr) => formatMeasurementTableTime(timeStr);

  const BG_CANONICAL_PERIODS = [
    'Wake-up Time',
    'Before-Breakfast',
    'After-Breakfast',
    'Before-Lunch',
    'After-Lunch',
    'Before-Dinner',
    'After-Dinner',
    'Bedtime',
    'Midnight'
  ];

  const mapToCanonicalBGPeriod = (name) => {
    const normalized = normalizePeriodName(name);
    if (!normalized) return null;
    return BG_CANONICAL_PERIODS.find((p) => normalizePeriodName(p) === normalized) || null;
  };

  const convertTimeToMinutes = (timeStr) => {
    if (!timeStr) return null;
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      const [time, meridiem] = timeStr.trim().split(' ');
      const [hh, mm] = time.split(':').map(Number);
      if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
      let hour = hh;
      if (meridiem?.toUpperCase() === 'PM' && hour !== 12) hour += 12;
      if (meridiem?.toUpperCase() === 'AM' && hour === 12) hour = 0;
      return hour * 60 + mm;
    }
    const [h, m] = timeStr.split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
  };

  const resolveTargetsBundleAtMs = useCallback(
    (ms) => {
      if (!scheduleTargetSources) return null;
      const usePatient = getUsePatientScheduleTargetsAtMeasurementMs(practiceId, patientId, ms);
      return resolveDisplayScheduleTargets({
        practiceData: scheduleTargetSources.practiceData,
        patientData: scheduleTargetSources.patientData,
        usePatientTargets: usePatient
      });
    },
    [scheduleTargetSources, practiceId, patientId, targetTimelineRev]
  );

  const resolvePeriodNameByTime = (measureTime, configuredOverride) => {
    const configured = Array.isArray(configuredOverride)
      ? configuredOverride
      : Array.isArray(scheduleTargets?.bloodGlucoseTimePeriod)
        ? scheduleTargets.bloodGlucoseTimePeriod
        : [];
    return determinePeriodFromTime(
      measureTime,
      configured,
      PERMANENT_START_TIMES.bloodGlucose,
      PERMANENT_END_TIMES.bloodGlucose
    );
  };

  // Determine period: prefer explicit labels from the device/API (period_name, measure_note, note)
  // so rows land in the intended column. Time-window matching is only a fallback when no label maps.
  const determinePeriodFromMeasurement = (measurement) => {
    const ms = getMeasurementEpochMs(measurement);
    const bundle = resolveTargetsBundleAtMs(ms) || scheduleTargets;
    const configuredPeriods = Array.isArray(bundle?.bloodGlucoseTimePeriod) ? bundle.bloodGlucoseTimePeriod : [];

    const normalizeToConfiguredPeriod = (value) => {
      if (!value) return null;
      const normalized = normalizePeriodName(value);
      const direct = periodNames.find((p) => normalizePeriodName(p) === normalized);
      if (direct) return direct;
      const canonical = mapToCanonicalBGPeriod(value);
      if (!canonical) return null;
      return periodNames.find((p) => normalizePeriodName(p) === normalizePeriodName(canonical)) || canonical;
    };

    const labelForResolve =
      measurement.period_name ||
      measurement.period ||
      measurement.measure_note ||
      measurement.note ||
      '';

    const storedPeriodName = labelForResolve
      ? resolvePeriodNameByIdOrLabel(configuredPeriods, {
          ...measurement,
          period_name: labelForResolve
        })
      : resolvePeriodNameByIdOrLabel(configuredPeriods, measurement);

    const resolvedStored = normalizeToConfiguredPeriod(storedPeriodName || labelForResolve);
    if (resolvedStored) return resolvedStored;

    const measureTime = getMeasurementWallClockTimeHms(measurement);

    if (measureTime) {
      const byTime = resolvePeriodNameByTime(measureTime, configuredPeriods);
      const resolvedByTime = normalizeToConfiguredPeriod(byTime);
      if (resolvedByTime) return resolvedByTime;
    }

    return null;
  };

  // Get period names from schedule targets
  const periodNames = useMemo(() => {
    const names = Array.isArray(scheduleTargets?.bloodGlucoseTimePeriod)
      ? scheduleTargets.bloodGlucoseTimePeriod.map((p) => p?.name).filter(Boolean)
      : [];
    const source = names.length > 0 ? names : BG_CANONICAL_PERIODS;
    const seen = new Set();
    return source.filter((name) => {
      const key = normalizePeriodName(name);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [scheduleTargets]);

  const getPeriodColor = () => '#F8FAFC';

  // Get period info (including time ranges) from schedule targets
  const getPeriodInfo = (periodName) => {
    if (!scheduleTargets?.bloodGlucoseTimePeriod) {
      return null;
    }
    
    // Map daily table period names to schedule target period names
    const searchName = mapToCanonicalBGPeriod(periodName) || periodName;
    const period = findPeriodByName(scheduleTargets.bloodGlucoseTimePeriod, searchName);
    
    if (!period) return null;
    
    // Get time and convert to 12-hour format for display
    const display = getPermanentPeriodDisplay(
      searchName,
      PERMANENT_START_TIMES.bloodGlucose,
      PERMANENT_END_TIMES.bloodGlucose,
      period
    );

    return {
      name: period.name,
      startTime: display.startTime,
      endTime: display.endTime,
      startTimeDisplay: display.startTimeDisplay,
      endTimeDisplay: display.endTimeDisplay
    };
  };

  // Get target for a period
  const getTargetForPeriod = (periodName, bundleOverride) => {
    const bundle = bundleOverride || scheduleTargets;
    if (!bundle?.bloodGlucoseTargetRange) return { min: 60, max: 110 };
    const targets = bundle.bloodGlucoseTargetRange;
    const normalizeTargetKey = (value) =>
      normalizePeriodName(value).replace(/targetranges?$/, '').replace(/targets?$/, '');
    const normalizedPeriod = normalizeTargetKey(periodName);
    const toRange = (target, fallbackMin, fallbackMax) => {
      const min = Number(target?.minimum ?? target?.min);
      const max = Number(target?.maximum ?? target?.max);
      return {
        min: Number.isNaN(min) ? fallbackMin : min,
        max: Number.isNaN(max) ? fallbackMax : max
      };
    };

    // Prefer period-specific target rows when present.
    const periodSpecific = targets.find((t) =>
      normalizeTargetKey(t.name).includes(normalizedPeriod)
    );
    if (periodSpecific) return toRange(periodSpecific, 60, 110);
    
    // Legacy Before-Meal / After-Meal shared targets
    if (
      normalizedPeriod === normalizeTargetKey('Before-Meal') ||
      ['beforebreakfast', 'beforelunch', 'beforedinner'].includes(normalizedPeriod)
    ) {
      const target = targets.find(t => 
        normalizeTargetKey(t.name).includes('beforemeal')
      ) || targets[0];
      return toRange(target, 60, 110);
    }
    
    if (
      normalizedPeriod === normalizeTargetKey('After-Meal') ||
      ['afterbreakfast', 'afterlunch', 'afterdinner'].includes(normalizedPeriod)
    ) {
      const target = targets.find(t => 
        normalizeTargetKey(t.name).includes('aftermeal')
      ) || targets[0];
      return toRange(target, 70, 140);
    }
    
    // Find target by period name or use first as default
    const period = bundle.bloodGlucoseTimePeriod?.find(
      (p) => normalizeTargetKey(p.name) === normalizedPeriod
    );
    const target = targets.find(
      t =>
        normalizeTargetKey(t.name) === normalizedPeriod ||
        normalizeTargetKey(t.name).includes(normalizedPeriod) ||
        normalizedPeriod.includes(normalizeTargetKey(t.name)) ||
        normalizeTargetKey(t.periodName) === normalizedPeriod ||
        t.id === period?.id
    ) || targets[0];
    
    const min = Number(target?.minimum ?? target?.min);
    const max = Number(target?.maximum ?? target?.max);
    return {
      min: Number.isNaN(min) ? 60 : min,
      max: Number.isNaN(max) ? 110 : max
    };
  };

  const effectiveMeasurements = useMemo(
    () => dedupeMeasurementsByDateTimeKeepLatest(measurements),
    [measurements]
  );

  // Group measurements by date
  const groupedMeasurements = useMemo(() => {
    const grouped = {};
    
    const sortedMeasurements = [...effectiveMeasurements].sort((a, b) => {
      const dateA = getMeasurementWallClockDateYmd(a);
      const dateB = getMeasurementWallClockDateYmd(b);
      
      if (dateA !== dateB) {
        return dateB.localeCompare(dateA);
      }
      
      const timeA = getMeasurementWallClockTimeHms(a) || '00:00:00';
      const timeB = getMeasurementWallClockTimeHms(b) || '00:00:00';
      return timeB.localeCompare(timeA);
    });

    // Deduplicate exact duplicates only:
    // same date+time + same glucose values => keep latest.
    // same date+time + different glucose values => keep both.
    const uniqueByDateTime = new Map();
    sortedMeasurements.forEach((m) => {
      const date = getMeasurementWallClockDateYmd(m);
      const time = (getMeasurementWallClockTimeHms(m) || '').slice(0, 5);
      const key = `${date}|${time}|${m.blood_glucose_value_1 ?? ''}|${m.blood_glucose_value_2 ?? ''}|${m.blood_glucose_value_3 ?? ''}|${m.blood_glucose_value ?? ''}`;
      if (!uniqueByDateTime.has(key)) {
        uniqueByDateTime.set(key, m);
      }
    });
    
    Array.from(uniqueByDateTime.values()).forEach(measurement => {
      const date = getMeasurementWallClockDateYmd(measurement);
      if (!date) return;
      
      if (!grouped[date]) {
        grouped[date] = [];
      }
      
      grouped[date].push(measurement);
    });
    
    return grouped;
  }, [effectiveMeasurements]);

  const sortedDates = useMemo(() => {
    return Object.keys(groupedMeasurements).sort((a, b) => b.localeCompare(a));
  }, [groupedMeasurements]);

  const dateBuckets = useMemo(() => {
    if (sortedDates.length === 0) return [];

    if (sortBy === 'All' || sortBy === 'Date') {
      return [{ id: 0, label: 'All Dates', dates: sortedDates }];
    }

    const daysPerBucket = {
      '1 Day Average': 1,
      'Weekly average': 7,
      '14 Days Average': 14,
      'Monthly average': 30,
      '3 Months Average': 90,
      'Daily average': 1
    }[sortBy] || 7;

    const newest = parseWallClockDateLocal(sortedDates[0]);
    if (!newest) return [{ id: 0, label: 'All Dates', dates: sortedDates }];
    newest.setHours(0, 0, 0, 0);
    const msPerDay = 24 * 60 * 60 * 1000;

    const buckets = new Map();
    sortedDates.forEach((dateStr) => {
      const d = parseWallClockDateLocal(dateStr);
      if (!d) return;
      d.setHours(0, 0, 0, 0);
      const diffDays = Math.floor((newest - d) / msPerDay);
      const idx = Math.floor(diffDays / daysPerBucket);
      if (!buckets.has(idx)) buckets.set(idx, []);
      buckets.get(idx).push(dateStr);
    });

    return Array.from(buckets.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([idx, dates]) => {
        const sorted = [...dates].sort((a, b) => b.localeCompare(a));
        const start = sorted[sorted.length - 1];
        const end = sorted[0];
        return {
          id: idx,
          label: `Date Range : ${formatDateIso(start)} ~ ${formatDateIso(end)}`,
          dates: sorted
        };
      });
  }, [sortedDates, sortBy]);

  // Calculate statistics for a period
  const calculatePeriodStats = (allMeasurements, periodName) => {
    const normalizedPeriod = normalizePeriodName(periodName);
    const periodMeasurements = allMeasurements.filter((m) => {
      const resolved = determinePeriodFromMeasurement(m);
      return resolved && normalizePeriodName(resolved) === normalizedPeriod;
    });

    if (!periodMeasurements || periodMeasurements.length === 0) {
      return { avg: null, max: null, min: null, count: 0, above: 0, below: 0, within: 0 };
    }

    const bgValues = periodMeasurements
      .map(m => parseFloat(m.blood_glucose_value_1 || m.blood_glucose_value))
      .filter(v => !isNaN(v));

    let above = 0, below = 0, within = 0;
    periodMeasurements.forEach(m => {
      const bgValue = m.blood_glucose_value_1 || m.blood_glucose_value;
      const bundle = resolveTargetsBundleAtMs(getMeasurementEpochMs(m)) || scheduleTargets;
      const target = getTargetForPeriod(periodName, bundle);
      const status = checkBGValue(bgValue, target);
      if (status === 'high') above++;
      else if (status === 'low') below++;
      else within++;
    });

    const bgAvg = bgValues.length > 0 ? bgValues.reduce((a, b) => a + b, 0) / bgValues.length : null;
    const bgMax = bgValues.length > 0 ? Math.max(...bgValues) : null;
    const bgMin = bgValues.length > 0 ? Math.min(...bgValues) : null;

    return {
      avg: bgAvg,
      max: bgMax,
      min: bgMin,
      count: periodMeasurements.length,
      above,
      below,
      within
    };
  };

  // Render all readings for one period/date inside same cells
  const renderPeriodMeasurementsCells = (periodMeasurements, periodName) => {
    if (!periodMeasurements || periodMeasurements.length === 0) {
      return (
        <>
          <td></td>
          <td></td>
        </>
      );
    }
    
    // Use the period name directly so period-specific targets are preferred.
    const targetPeriodName = periodName;

    const readings = [...periodMeasurements].sort((a, b) => {
      const timeA = getMeasurementWallClockTimeHms(a) || '00:00:00';
      const timeB = getMeasurementWallClockTimeHms(b) || '00:00:00';
      return timeB.localeCompare(timeA);
    });

    return (
      <>
        <td>
          <div className="bg-cell-stack">
            {readings.map((measurement, index) => {
              const bgValue = measurement.blood_glucose_value_1 || measurement.blood_glucose_value;
              if (bgValue == null || bgValue === '') return <div key={`${measurement.id || index}-bg`}></div>;
              const bundle =
                resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
              const target = getTargetForPeriod(targetPeriodName, bundle);
              const status = checkBGValue(bgValue, target);
              const cellClass = getMeasurementValueClass(status);
              return (
                <div key={`${measurement.id || index}-bg`} className={cellClass}>
                  {Math.round(parseFloat(bgValue))}
                </div>
              );
            })}
          </div>
        </td>
        <td>
          <div className="bg-cell-stack">
            {readings.map((measurement, index) => {
              const measureTime = getMeasurementWallClockTimeHms(measurement);
              const formattedTime = measureTime ? formatTime(measureTime) : '';
              return <div key={`${measurement.id || index}-time`} className="bg-time-text">{formattedTime}</div>;
            })}
          </div>
        </td>
      </>
    );
  };

  const calculateOverallTargetStats = () => {
    const stats = { above: 0, below: 0, within: 0 };
    effectiveMeasurements.forEach((m) => {
      const period = determinePeriodFromMeasurement(m);
      const bundle = resolveTargetsBundleAtMs(getMeasurementEpochMs(m)) || scheduleTargets;
      const target = getTargetForPeriod(period, bundle);
      const raw = m.blood_glucose_value_1 ?? m.blood_glucose_value;
      const value = raw != null ? Number(raw) : null;
      if (value == null || Number.isNaN(value)) return;
      const status = checkBGValue(value, target);
      if (status === 'high') stats.above += 1;
      else if (status === 'low') stats.below += 1;
      else stats.within += 1;
    });
    return stats;
  };

  const overallStats = calculateOverallTargetStats();
  const totalReadingCount = overallStats.above + overallStats.below + overallStats.within;

  const formatPercentCount = (count, total) => {
    if (!total) return `0% (${count})`;
    const pct = Math.round((count / total) * 100);
    return `${pct}% (${count})`;
  };

  const getSummaryPeriodMeta = (periodName) => {
    const key = normalizePeriodName(periodName);
    if (key.includes('wakeup')) return { group: 'Wake-up', sub: null, isSingleHeader: true };
    if (key.includes('beforebreakfast')) return { group: 'Breakfast', sub: 'Before-Meal' };
    if (key.includes('afterbreakfast')) return { group: 'Breakfast', sub: 'After-Meal' };
    if (key.includes('beforelunch')) return { group: 'Lunch', sub: 'Before-Meal' };
    if (key.includes('afterlunch')) return { group: 'Lunch', sub: 'After-Meal' };
    if (key.includes('beforedinner')) return { group: 'Dinner', sub: 'Before-Meal' };
    if (key.includes('afterdinner')) return { group: 'Dinner', sub: 'After-Meal' };
    if (key.includes('bedtime')) return { group: 'Bedtime', sub: null, isSingleHeader: true };
    if (key.includes('midnight')) return { group: 'Midnight', sub: null, isSingleHeader: true };
    return { group: periodName, sub: periodName, isSingleHeader: false };
  };

  const summaryPeriodColumns = useMemo(
    () => periodNames.map((periodName) => ({ periodName, ...getSummaryPeriodMeta(periodName) })),
    [periodNames]
  );

  const summaryHeaderGroups = useMemo(() => {
    const groups = [];
    summaryPeriodColumns.forEach((meta) => {
      const existing = groups.find((g) => g.group === meta.group);
      const item = { periodName: meta.periodName, sub: meta.sub, isSingleHeader: !!meta.isSingleHeader };
      if (existing) existing.items.push(item);
      else groups.push({ group: meta.group, items: [item] });
    });
    return groups;
  }, [summaryPeriodColumns]);

  const totalRowLayout = useMemo(() => {
    const beforeMealCols = summaryPeriodColumns.filter((col) => col.sub === 'Before-Meal').length;
    const afterMealCols = summaryPeriodColumns.filter((col) => col.sub === 'After-Meal').length;
    const remainingCols = Math.max(summaryPeriodColumns.length - beforeMealCols - afterMealCols, 0);
    return {
      beforeMealCols: Math.max(beforeMealCols, 1),
      afterMealCols: Math.max(afterMealCols, 1),
      remainingCols: Math.max(remainingCols, 1)
    };
  }, [summaryPeriodColumns]);

  const rangeTotals = useMemo(
    () => ({
      above: overallStats.above,
      below: overallStats.below,
      within: overallStats.within
    }),
    [overallStats]
  );

  const getPeriodBadgeClass = (periodName) => {
    const n = normalizePeriodName(periodName);
    if (n.includes('wake')) return 'wakeup';
    if (n.includes('morning') || n.includes('breakfast')) return 'morning';
    if (n.includes('noon') || n.includes('lunch')) return 'noon';
    if (n.includes('afternoon') || n.includes('dinner')) return 'afternoon';
    if (n.includes('evening') || n.includes('bed') || n.includes('midnight') || n.includes('night')) return 'evening';
    return 'other';
  };

  const resolveBgTargetPeriodName = (periodName) => periodName;

  const getReadingTime = (measurement) => {
    const measureTime = getMeasurementWallClockTimeHms(measurement);
    return measureTime ? formatTime(measureTime) : '';
  };

  const sortReadingsNewestFirst = (arr) =>
    [...(arr || [])].sort((a, b) => {
      const timeA = getMeasurementWallClockTimeHms(a) || '00:00:00';
      const timeB = getMeasurementWallClockTimeHms(b) || '00:00:00';
      return timeB.localeCompare(timeA);
    });

  const getDatePeriodMap = (date) => {
    const dateMeasurements = groupedMeasurements[date] || [];
    const byPeriod = {};
    dateMeasurements.forEach((m) => {
      const period = determinePeriodFromMeasurement(m);
      const matchedPeriod = periodNames.find(
        (p) => normalizePeriodName(p) === normalizePeriodName(period || '')
      );
      if (!matchedPeriod) return;
      if (!byPeriod[matchedPeriod]) byPeriod[matchedPeriod] = [];
      byPeriod[matchedPeriod].push(m);
    });
    return byPeriod;
  };

  const getReadingLevelClass = (status) => {
    if (status === 'high') return 'high';
    if (status === 'low') return 'low';
    if (status === 'normal') return 'within';
    return '';
  };

  const getReadingBgStatus = (measurement, periodName) => {
    const bgValue = measurement.blood_glucose_value_1 || measurement.blood_glucose_value;
    const bundle = resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
    const target = getTargetForPeriod(resolveBgTargetPeriodName(periodName), bundle);
    return checkBGValue(bgValue, target);
  };

  const dateRangeLabel = useMemo(() => {
    if (!sortedDates.length) return '';
    const newest = sortedDates[0];
    const oldest = sortedDates[sortedDates.length - 1];
    if (newest === oldest) return formatDate(newest);
    return `${formatDate(oldest)} – ${formatDate(newest)}`;
  }, [sortedDates]);

  const clearHoverHideTimer = () => {
    if (hoverHideTimerRef.current) {
      clearTimeout(hoverHideTimerRef.current);
      hoverHideTimerRef.current = null;
    }
  };

  const scheduleHoverHide = () => {
    clearHoverHideTimer();
    hoverHideTimerRef.current = setTimeout(() => {
      setHoverPreview(null);
      hoverHideTimerRef.current = null;
    }, 180);
  };

  const showHoverPreviewAt = (event, readings, periodName) => {
    if (!readings || readings.length < 2) return;
    clearHoverHideTimer();
    const sorted = sortReadingsNewestFirst(readings);
    const others = sorted.slice(1);
    const bgVals = sorted
      .map((r) => Number(r.blood_glucose_value_1 ?? r.blood_glucose_value))
      .filter((n) => !Number.isNaN(n));
    const avgBg = bgVals.length
      ? Math.round(bgVals.reduce((a, b) => a + b, 0) / bgVals.length)
      : null;
    const x = event.clientX + 12;
    const y = event.clientY + 12;
    setHoverPreview({
      x: Math.min(x, window.innerWidth - 220),
      y: Math.min(y, window.innerHeight - 120),
      title: `${String(periodName || 'Period').toUpperCase()} • EARLIER READINGS`,
      periodName,
      totalCount: sorted.length,
      averageLabel: avgBg != null ? String(avgBg) : '—',
      readings: others
    });
  };

  useEffect(() => () => clearHoverHideTimer(), []);
  useEffect(() => {
    if (!showExportMenu) return undefined;
    const handleClickOutside = (event) => {
      if (!exportMenuRef.current?.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  // Get patient info for display
  const patient = patientData?.patient;
  const patientInfo = useMemo(() => {
    const patientNameLine = patient ? `${patient.last_name || ''}, ${patient.first_name || ''}`.trim() : 'Loading...';
    const patientDOB = patient?.date_of_birth
      ? `${formatDate(patient.date_of_birth)} (${new Date().getFullYear() - new Date(patient.date_of_birth).getFullYear()} years)`
      : 'N/A';
    const patientPhone = formatPhoneForDisplay(patient?.phone_number, 'N/A');
    const patientDevice = patientData?.device_serial || 'N/A';
    return { patientNameLine, patientDOB, patientPhone, patientDevice };
  }, [patient, patientData?.device_serial]);
  const { patientNameLine, patientDOB, patientPhone, patientDevice } = patientInfo;

  // Set breadcrumbs (must be after patientName is declared)
  useEffect(() => {
    if (patientData) {
      const fromDashboard = location.state?.fromDashboard;
      const rootCrumb = { label: 'Dashboard', path: '/dashboard' };
      const basePath = fromDashboard ? '/dashboard' : '/practices';

      updateBreadcrumbs([
        rootCrumb,
        { label: patientData?.patient?.practice_name || patientData?.practice?.practice_name || 'Practice', path: `${basePath}/${practiceId}`, state: { fromDashboard } },
        { label: 'Patients', path: `${basePath}/${practiceId}?tab=patient`, state: { fromDashboard } },

        { label: patientNameLine, path: `/practices/${practiceId}/patients/${patientId}`, state: { fromDashboard } },
        { label: 'Summary Of Measurement', path: null, active: true }
      ]);
    }
  }, [patientData, practiceId, patientId, patientNameLine, updateBreadcrumbs, location.state]);


  const reportTitle = `Blood Glucose Report - ${patientNameLine}`;
  const exportFilename = `BG-Report-${patientNameLine.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;

  const handlePrint = () => {
    try {
      const el = document.getElementById('bg-report-content');
      printElement(el, reportTitle);
    } catch (e) {
      toast.error(e?.message || 'Print failed');
    }
  };

  const handlePdfExport = async () => {
    if (effectiveMeasurements.length === 0) {
      toast.warning('No measurement data to export');
      return;
    }
    try {
      setIsExporting(true);
      exportBloodGlucoseTablesPdf({
        filename: exportFilename,
        title: reportTitle,
        patient: { name: patientNameLine, dob: patientDOB, phone: patientPhone, device: patientDevice },
        dateBuckets,
        periodNames,
        groupedMeasurements,
        determinePeriodFromMeasurement,
        formatDate,
        formatTime,
        summaryPeriodColumns,
        calculatePeriodStats,
        effectiveMeasurements,
        rangeTotals,
        totalReadingCount,
        formatPercentCount,
        totalRowLayout
      });
      toast.success('Exported to PDF');
      patientService.logPatientExport(practiceId, patientId, { format: 'pdf', source: 'Blood Glucose' });
    } catch (e) {
      toast.error(e?.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCsvExport = () => {
    if (effectiveMeasurements.length === 0) {
      toast.warning('No measurement data to export');
      return;
    }
    const rows = [['Date', 'Period', 'Blood Glucose (mg/dL)', 'Measure Time']];
    sortedDates.forEach((date) => {
      const byPeriod = getDatePeriodMap(date);
      periodNames.forEach((periodName) => {
        const readings = sortReadingsNewestFirst(byPeriod[periodName] || []);
        readings.forEach((reading) => {
          const bgValue = reading.blood_glucose_value_1 ?? reading.blood_glucose_value ?? '';
          const measureTime = getMeasurementWallClockTimeHms(reading);
          rows.push([
            formatDate(date),
            periodName,
            bgValue === '' ? '' : Math.round(Number(bgValue)),
            measureTime ? formatTime(measureTime) : ''
          ]);
        });
      });
    });
    downloadBlob(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' }), exportFilename.replace(/\.pdf$/i, '.csv'));
    toast.success('Exported to CSV');
    patientService.logPatientExport(practiceId, patientId, { format: 'csv', source: 'Blood Glucose' });
  };

  const defaultEmailSubject = `Blood Glucose Report - ${patientNameLine} (${new Date().toLocaleDateString()})`;
  const defaultEmailContent = `Blood Glucose Report\nPatient: ${patientNameLine} (DOB: ${patientDOB})\nGenerated: ${new Date().toLocaleString()}\nTotal Readings: ${effectiveMeasurements.length}`;
  // Keep controls visible on tab switch (same UX as BP screen).
  // Table/summary have their own loading skeletons via queryLoading.
  const showInitialSkeleton = false;

  return (
    <div className="blood-glucose-page">
      {!useSharedControls && practiceIsLocked && (
        <div className="practice-locked-measurement-banner" role="status">
          This practice is locked. Historical readings remain available for up to 30 days. New measurements and
          follow-ups are not allowed.
        </div>
      )}
      <div id="bg-report-content" style={{ padding: '0px' }}>
      {showInitialSkeleton ? (
        <div className="bg-skeleton-page">
          <div className="bg-skeleton-block bg-skeleton-layout">
            <div className="bg-skeleton-line bg-w-40" />
            <div className="bg-skeleton-line bg-w-80" />
            <div className="bg-skeleton-line bg-w-60" />
          </div>
          <div className="bg-skeleton-block bg-skeleton-query">
            <div className="bg-skeleton-line bg-w-40" />
            <div className="bg-skeleton-line bg-w-80" />
            <div className="bg-skeleton-line bg-w-60" />
          </div>
          <div className="bg-skeleton-block bg-skeleton-table">
            <div className="bg-table-skeleton">
              <div className="bg-table-skel-head" />
              <div className="bg-table-skel-row" />
              <div className="bg-table-skel-row" />
              <div className="bg-table-skel-row" />
            </div>
          </div>
          <div className="bg-skeleton-block bg-skeleton-summary">
            <div className="bg-table-skeleton">
              <div className="bg-table-skel-head" />
              <div className="bg-table-skel-row" />
              <div className="bg-table-skel-row" />
            </div>
          </div>
        </div>
      ) : (
      <>
      
      {!useSharedControls && (
      <div className="bp-section bp-section--controls">
          <div className="bp-controls-header">
            <div className="bp-controls-header-left">
              <h2 className="bp-section-title">Measurement Controls</h2>
              <div className="bp-patient-chips">
                <div className="bp-chip">
                  <div className="chip-text">
                    <span className="chip-label">Name</span>
                    <span className="chip-value">{patientNameLine}</span>
                  </div>
                </div>
                <div className="bp-chip">
                  <div className="chip-text">
                    <span className="chip-label">DOB</span>
                    <span className="chip-value">{patientDOB}</span>
                  </div>
                </div>
                <div className="bp-chip">
                  <div className="chip-text">
                    <span className="chip-label">Phone</span>
                    <span className="chip-value">{patientPhone}</span>
                  </div>
                </div>
                <div className="bp-chip">
                  <div className="chip-text">
                    <span className="chip-label">Device</span>
                    <span className="chip-value">{patientDevice}</span>
                  </div>
                </div>
              </div>
            </div>
            {!practiceIsLocked && (
              <div className="bp-controls-header-right" data-export-ignore="true">
                <div className="bp-timer-block">
                  <div className="bp-timer-text">
                    <span className="bp-timer-label">Active Session</span>
                    <span className="bp-timer-value">{formatTimer(timer)}</span>
                  </div>
                  <div className="bp-timer-actions">
                    <button
                      type="button"
                      className={`bp-timer-btn bp-timer-toggle ${isRunning ? 'running' : 'paused'}`}
                      onClick={() => setIsRunning((prev) => !prev)}
                      title={isRunning ? 'Pause Timer' : 'Start Timer'}
                      aria-label={isRunning ? 'Pause Timer' : 'Start Timer'}
                    >
                      <FontAwesomeIcon icon={isRunning ? faPause : faPlay} />
                    </button>
                    <button
                      type="button"
                      className="bp-timer-btn bp-timer-close"
                      onClick={() => {
                        setIsRunning(false);
                        setTimer(0);
                      }}
                      title="Reset Timer"
                      aria-label="Reset Timer"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="bp-controls-nav" data-export-ignore="true">
            <VitalsTabs
              practiceId={practiceId}
              patientId={patientId}
              vitals={patientVitals}
              active="blood-glucose"
              mode="measurement"
              className="bp-controls-vitals-tabs"
            />
            {!practiceIsLocked && (
              <button
                className="bp-add-followup-btn"
                type="button"
                onClick={() => {
                  setIsRunning(false);
                  setShowFollowUpModal(true);
                }}
              >
                Add Follow Up
              </button>
            )}
          </div>
        </div>
      )}

      {/* Query Section */}
      <div className="bp-section bp-section--query" data-export-ignore="true">
        <div className="bp-section-title-row">
          <h2 className="bp-section-title">Query Period</h2>
        </div>
        <div className="bp-card">
          <div className="bp-card-body">
            <div className="bp-query-body">
              <div>
                <label className={`bp-option-card${queryType === 'dateRange' ? ' is-selected' : ''}`}>
                  <input
                    type="radio"
                    name="bg-queryType"
                    value="dateRange"
                    checked={queryType === 'dateRange'}
                    onChange={() => handleQueryTypeChange('dateRange')}
                  />
                  <span className="bp-option-label">Date Range</span>
                  <select
                    className="bp-dropdown"
                    value={dateRange}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDateRange(value);
                      if (String(value).toUpperCase() === 'ALL') {
                        setFromDate('');
                        setToDate('');
                      }
                    }}
                    disabled={queryType !== 'dateRange'}
                  >
                    <option>Last 30 days</option>
                    <option>Last 7 days</option>
                    <option>Last 14 days</option>
                    <option>Last 60 days</option>
                    <option>Last 90 days</option>
                    <option>All</option>
                  </select>
                </label>
                <label className={`bp-option-card${queryType === 'from' ? ' is-selected' : ''}`}>
                  <input
                    type="radio"
                    name="bg-queryType"
                    value="from"
                    checked={queryType === 'from'}
                    onChange={() => handleQueryTypeChange('from')}
                  />
                  <span className="bp-option-label">From / To</span>
                  <div className="bp-date-inputs" onClick={(e) => e.stopPropagation()}>
                    <AppDateInput
                      className="bp-date-input"
                      value={fromDate}
                      max={getMeasurementQueryMaxDate()}
                      onChange={(nextFrom) => {
                        const next = applyMeasurementFromDateChange(nextFrom, toDate);
                        setFromDate(next.fromDate);
                        setToDate(next.toDate);
                      }}
                      disabled={queryType !== 'from'}
                    />
                    <span className="bp-date-separator">to</span>
                    <AppDateInput
                      className="bp-date-input"
                      value={toDate}
                      min={fromDate || undefined}
                      max={getMeasurementQueryMaxDate()}
                      onChange={(nextTo) => setToDate(sanitizeMeasurementToDate(nextTo, fromDate))}
                      disabled={queryType !== 'from'}
                    />
                  </div>
                </label>
              </div>
              <div>
                <div className="bp-sort-block">
                  <label htmlFor="bg-sort-select">Sort Data By</label>
                  <select
                    id="bg-sort-select"
                    className="bp-dropdown"
                    style={{ width: '100%' }}
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option>Weekly average</option>
                    <option>Daily average</option>
                    <option>Monthly average</option>
                    <option>Date</option>
                  </select>
                </div>
                <div className="bp-action-buttons-row">
                  <button className="bp-action-btn primary" type="button" onClick={handleQuery} disabled={isQueryDisabled}>
                    Query
                  </button>
                  <button className="bp-action-btn" type="button" onClick={handleReset} disabled={isResetDisabled}>
                    Reset
                  </button>
                  {!practiceIsLocked && (
                    <button
                      className={`bp-action-btn${!canAddThisVital ? ' bp-action-btn--muted' : ''}`}
                      type="button"
                      disabled={!canAddThisVital}
                      title={!canAddThisVital ? PRACTICE_VITAL_ADD_DISABLED_TITLE : undefined}
                      onClick={() => {
                        if (!canAddThisVital) return;
                        setShowAddModal(true);
                      }}
                    >
                      Add Data
                    </button>
                  )}
                  <button className="bp-action-btn" type="button" onClick={handlePrint}>
                    Print
                  </button>
                  <div className="bp-export-container" ref={exportMenuRef}>
                    <button
                      className="bp-action-btn"
                      type="button"
                      onClick={() => setShowExportMenu((prev) => !prev)}
                      disabled={isExporting}
                      aria-haspopup="menu"
                      aria-expanded={showExportMenu}
                    >
                      {isExporting ? 'Exporting...' : 'Export'} <span className="bp-export-caret">▾</span>
                    </button>
                    <div className={`bp-export-menu${showExportMenu ? ' show' : ''}`} role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setShowExportMenu(false);
                          handlePdfExport();
                        }}
                      >
                        PDF
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setShowExportMenu(false);
                          handleCsvExport();
                        }}
                      >
                        CSV
                      </button>
                    </div>
                  </div>
                  <button className="bp-action-btn" type="button" onClick={() => setShowEmailModal(true)}>
                    Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Measurement Log Table */}
      <div className="bp-section">
        <div className="bp-section-title-row">
          <h2 className="bp-section-title">Blood Glucose Measurements</h2>
          {dateRangeLabel && (
            <span className="bp-section-meta">{dateRangeLabel} · hover a cell to see every reading</span>
          )}
        </div>
        <div className="bp-card bp-card--table">
          <div className="bp-card-body bp-card-body--flush">
            {queryLoading ? (
              <div className="bp-empty-state">
                <p className="bp-empty-title">Loading measurements…</p>
              </div>
            ) : effectiveMeasurements.length === 0 ? (
              <div className="bp-empty-state">
                <p className="bp-empty-title">No measurement data</p>
                <p className="bp-empty-sub">No measurement data available for the selected period.</p>
              </div>
            ) : (
              <div className="bphr-grid-container">
                <table className="bphr-table">
                  <colgroup>
                    <col style={{ width: '100px' }} />
                    {periodNames.map((periodName) => (
                      <col key={periodName} style={{ width: '140px' }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      <th rowSpan={2} className="datehd">Date</th>
                      {periodNames.map((periodName) => {
                        const periodInfo = getPeriodInfo(periodName);
                        const timeRange =
                          periodInfo?.startTimeDisplay && periodInfo?.endTimeDisplay
                            ? `${periodInfo.startTimeDisplay}–${periodInfo.endTimeDisplay}`
                            : '';
                        return (
                          <th key={periodName} className="grp">
                            {periodName}
                            {timeRange ? <span className="hds">{timeRange}</span> : null}
                          </th>
                        );
                      })}
                    </tr>
                    <tr>
                      {periodNames.map((periodName) => (
                        <th key={`${periodName}-sub`} className="sub">
                          Glucose <span className="sub-unit">(mg/dL)</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDates.map((date) => {
                      const byPeriod = getDatePeriodMap(date);
                      return (
                        <tr key={date} className="reading-row">
                          <td className="cell date-col">{formatDate(date)}</td>
                          {periodNames.map((periodName) => {
                            const readings = sortReadingsNewestFirst(byPeriod[periodName] || []);
                            if (!readings.length) {
                              return (
                                <td key={periodName} className="cell metric">
                                  <span className="empty">—</span>
                                </td>
                              );
                            }
                            return (
                              <td key={periodName} className="cell metric">
                                {readings.map((r, idx) => {
                                  const levelClass = getReadingLevelClass(getReadingBgStatus(r, periodName));
                                  const bgRaw = r.blood_glucose_value_1 ?? r.blood_glucose_value;
                                  const bgVal = bgRaw != null && bgRaw !== '' ? Math.round(Number(bgRaw)) : '—';
                                  const timeStr = getReadingTime(r);
                                  return (
                                    <React.Fragment key={r.id || idx}>
                                      {idx > 0 && <div className="dash" />}
                                      <div className="bphr-metric">
                                        <div className="bphr-metric-value">
                                          <span className={levelClass}>{bgVal}</span>
                                        </div>
                                        {timeStr ? <div className="bphr-metric-label">{timeStr}</div> : null}
                                      </div>
                                    </React.Fragment>
                                  );
                                })}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Statistics Table */}
      <div className="bp-section">
        <div className="bp-section-title-row">
          <h2 className="bp-section-title">Summary Of Measurement</h2>
          <div className="bp-target-legend">
            <span><span className="dot above" />Above</span>
            <span><span className="dot within" />Within</span>
            <span><span className="dot below" />Below</span>
          </div>
        </div>
        <div className="bp-card bp-card--table">
          <div className="bp-card-body bp-card-body--flush">
            {queryLoading ? (
              <div className="bp-empty-state"><p className="bp-empty-title">Loading summary…</p></div>
            ) : (
              <>
                <table className="bp-simple-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th className="num">Glucose (avg / max / min)</th>
                      <th className="num">Count</th>
                      <th>Target Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periodNames.map((periodName) => {
                      const periodInfo = getPeriodInfo(periodName);
                      const timeRange =
                        periodInfo?.startTimeDisplay && periodInfo?.endTimeDisplay
                          ? `${periodInfo.startTimeDisplay}–${periodInfo.endTimeDisplay}`
                          : '';
                      const stats = calculatePeriodStats(effectiveMeasurements, periodName);
                      const totalSeg = (stats.above || 0) + (stats.within || 0) + (stats.below || 0);
                      const fmt = (v) => (v != null && !Number.isNaN(v) ? Math.round(v) : '—');
                      const hasData = (stats.count || 0) > 0;
                      return (
                        <tr key={periodName}>
                          <td>
                            <span className={`bp-period-badge ${getPeriodBadgeClass(periodName)}`}>
                              <span className="pb-name">{periodName}</span>
                              {timeRange ? <span className="pb-time">{timeRange}</span> : null}
                            </span>
                          </td>
                          {hasData ? (
                            <>
                              <td className="num">
                                <div className="bp-mini-minmax">
                                  <span className="mm-avg">{fmt(stats.avg)}</span>{' '}
                                  <span className="mm-range">({fmt(stats.min)}–{fmt(stats.max)})</span>
                                </div>
                              </td>
                              <td className="num">{stats.count}</td>
                            </>
                          ) : (
                            <td className="num bp-no-data-inline" colSpan={2}>No readings</td>
                          )}
                          <td className="bp-target-bar-cell">
                            <div className="bp-target-seg-bar">
                              {totalSeg > 0 ? (
                                <>
                                  <div className="seg above" style={{ width: `${(stats.above / totalSeg) * 100}%` }} />
                                  <div className="seg within" style={{ width: `${(stats.within / totalSeg) * 100}%` }} />
                                  <div className="seg below" style={{ width: `${(stats.below / totalSeg) * 100}%` }} />
                                </>
                              ) : (
                                <div className="seg" style={{ width: '100%', background: '#F1F0EC' }} />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="bp-total-grid">
                  <div className="bp-total-card above">
                    <div className="bp-total-title">Above target</div>
                    <div className="stat-line">Glucose: {formatPercentCount(rangeTotals.above, totalReadingCount)}</div>
                  </div>
                  <div className="bp-total-card within">
                    <div className="bp-total-title">Within target</div>
                    <div className="stat-line">Glucose: {formatPercentCount(rangeTotals.within, totalReadingCount)}</div>
                  </div>
                  <div className="bp-total-card below">
                    <div className="bp-total-title">Below target</div>
                    <div className="stat-line">Glucose: {formatPercentCount(rangeTotals.below, totalReadingCount)}</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Add Blood Glucose Modal */}
      {!practiceIsLocked && canAddThisVital && showAddModal && (
        <AddBGForm
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          practiceId={practiceId}
          patientId={patientId}
          onSuccess={handleAddDataSuccess}
          initialScheduleTargets={scheduleTargets}
        />
      )}

      {!useSharedControls && !practiceIsLocked && showFollowUpModal && (
        <AddFollowUpModal
          isOpen={showFollowUpModal}
          onClose={() => setShowFollowUpModal(false)}
          practiceId={practiceId}
          patientId={patientId}
          patientName={patientNameLine}
          initialTimerValue={timer}
          onTimerSync={(seconds) => {
            setTimer(Math.max(0, Number(seconds) || 0));
            setIsRunning(true);
          }}
          onSuccess={() => {}}
        />
      )}
      </>
      )}
      </div>{/* end bg-report-content */}

      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        defaultSubject={defaultEmailSubject}
        defaultContent={defaultEmailContent}
        onSend={async (emailData) => {
          try {
            setEmailSending(true);
            const res = await sendReportEmail(emailData, { event_id: 10 });
            if (!res.success) throw new Error(res.error || res.message);
            toast.success('Email queued successfully');
            patientService.logPatientExport(practiceId, patientId, {
              format: 'email',
              source: 'Blood Glucose'
            });
            setShowEmailModal(false);
          } catch (e) {
            toast.error(e?.message || 'Failed to send email');
          } finally {
            setEmailSending(false);
          }
        }}
      />
    </div>
  );
};

export default BloodGlucose;

